import { useEffect, useState } from "react";
import { collection, onSnapshot, orderBy, query, where, Timestamp } from "firebase/firestore";
import { db } from "./firebase";

export type TenantSummary = {
  tenantId: string;
  name: string;
  status: "active" | "suspended" | "deleted";
  themePrimary: string;
  logoUrl: string | null;
  verificationStatus: "pending" | "pending_review" | "verified" | "rejected" | null;
  legalCountry: string | null;
  subscriptionStatus: "authorized" | "pending" | "cancelled" | "none";
  subscriptionAmount: number | null;
  customers: number;
  ambassadors: number;
  tenantAdmins: number;
  todayPurchases: number;
  createdAt: Date | null;
};

/**
 * Stats agregados por tenant para la vista platform_admin. Live updates.
 * NOTA: hace N+1 reads (1 per tenant + 4 subqueries). OK para <50 tenants.
 * Para mayor scale conviene mover a una callable que pre-agregue.
 */
export function usePlatformTenantsSummary(includeDeleted = false): {
  tenants: TenantSummary[];
  loading: boolean;
} {
  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "tenants"), orderBy("createdAt", "desc"));
    const unsubsByTenant = new Map<string, Array<() => void>>();

    const main = onSnapshot(q, (snap) => {
      const map = new Map<string, TenantSummary>();
      snap.docs.forEach((d) => {
        const x = d.data();
        const status = (x.status as TenantSummary["status"]) ?? "active";
        if (!includeDeleted && status === "deleted") return;
        map.set(d.id, {
          tenantId: d.id,
          name: (x.name as string) ?? d.id,
          status,
          themePrimary: (x.theme?.primary as string) ?? "#4a6921",
          logoUrl: (x.theme?.logoUrl as string | null) ?? null,
          verificationStatus: (x.verificationStatus as TenantSummary["verificationStatus"]) ?? null,
          legalCountry: (x.legal?.country as string | null) ?? null,
          subscriptionStatus: "none",
          subscriptionAmount: null,
          customers: 0,
          ambassadors: 0,
          tenantAdmins: 0,
          todayPurchases: 0,
          createdAt: (x.createdAt as { toDate?: () => Date })?.toDate?.() ?? null
        });
      });

      // Clean up listeners for tenants that disappeared
      for (const [tid, fns] of unsubsByTenant) {
        if (!map.has(tid)) {
          fns.forEach((f) => f());
          unsubsByTenant.delete(tid);
        }
      }

      // Set up new listeners
      map.forEach((_, tid) => {
        if (unsubsByTenant.has(tid)) return;
        const subs: Array<() => void> = [];

        // customers
        subs.push(
          onSnapshot(
            query(collection(db, "tenants", tid, "users"), where("role", "==", "customer")),
            (s) =>
              setTenants((prev) =>
                prev.map((t) => (t.tenantId === tid ? { ...t, customers: s.size } : t))
              )
          )
        );

        // ambassadors
        subs.push(
          onSnapshot(
            query(collection(db, "tenants", tid, "users"), where("role", "==", "ambassador")),
            (s) =>
              setTenants((prev) =>
                prev.map((t) => (t.tenantId === tid ? { ...t, ambassadors: s.size } : t))
              )
          )
        );

        // tenantAdmins
        subs.push(
          onSnapshot(
            query(collection(db, "tenants", tid, "users"), where("role", "==", "tenant_admin")),
            (s) =>
              setTenants((prev) =>
                prev.map((t) => (t.tenantId === tid ? { ...t, tenantAdmins: s.size } : t))
              )
          )
        );

        // today purchases (last 24h)
        const since = Timestamp.fromDate(new Date(Date.now() - 24 * 3600 * 1000));
        subs.push(
          onSnapshot(
            query(collection(db, "tenants", tid, "purchases"), where("createdAt", ">=", since)),
            (s) =>
              setTenants((prev) =>
                prev.map((t) => (t.tenantId === tid ? { ...t, todayPurchases: s.size } : t))
              )
          )
        );

        // latest subscription
        subs.push(
          onSnapshot(
            query(
              collection(db, "tenants", tid, "subscriptions"),
              orderBy("createdAt", "desc")
            ),
            (s) => {
              const docs = s.docs.map((d) => d.data());
              const authorized = docs.find((d) => d.status === "authorized");
              const latest = docs[0];
              const target = authorized ?? latest;
              setTenants((prev) =>
                prev.map((t) =>
                  t.tenantId === tid
                    ? {
                        ...t,
                        subscriptionStatus: (target?.status as TenantSummary["subscriptionStatus"]) ?? "none",
                        subscriptionAmount:
                          typeof target?.amount === "number" ? (target.amount as number) : null
                      }
                    : t
                )
              );
            }
          )
        );

        unsubsByTenant.set(tid, subs);
      });

      setTenants(Array.from(map.values()));
      setLoading(false);
    });

    return () => {
      main();
      unsubsByTenant.forEach((fns) => fns.forEach((f) => f()));
      unsubsByTenant.clear();
    };
  }, [includeDeleted]);

  return { tenants, loading };
}
